import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Array de imágenes para el slider
  const slides = [
    {
      id: 1,
      src: '/assets/hero/Banner 5.png',
      alt: 'Background 1',
    },
    {
      id: 2,
      src: '/assets/hero/Banner 3.JPG',
      alt: 'Background 2',
    },
    {
      id: 3,
      src: '/assets/hero/Banner 2.png',
      alt: 'Background 3',
    },
    {
      id: 4,
      src: '/assets/hero/Banner 4.png',
      alt: 'Background 4',
    },
    {
      id: 5,
      src: '/assets/hero/Banner 6.jpg',
      alt: 'Background 5',
    },
  ];

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cambiar automáticamente de slide cada 5 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section
      className={`relative ${isMobile ? 'flex flex-col' : 'h-screen flex items-end'} overflow-hidden pt-3 md:pt-16`}
    >
      {/* Contenedor del slider - Diferente comportamiento en móvil y desktop */}
      {isMobile ? (
        // Versión móvil: imágenes arriba, contenido debajo
        <>
          <div className="relative w-full h-[40vh] overflow-hidden">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute top-0 left-0 w-full h-full transition-all duration-1000 ease-in-out ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70"></div>
              </div>
            ))}

            {/* Indicadores del slider */}
            <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'w-6 bg-orange-500' : 'bg-white/60'
                  }`}
                  aria-label={`Ir a slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Contenido debajo de la imagen en móvil */}
          <div className="container relative z-20 mx-auto px-4 py-8 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-2xl mx-auto text-center">
              <h1 className="text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
                Construye tu carrera laboral con Packsys
              </h1>

              <p className="text-gray-600 mb-6">
                Encuentra las mejores oportunidades laborales y forma parte de
                nuestro equipo
              </p>

              <div className="flex flex-col gap-3">
                <Link
                  to="/vacantes"
                  className="btn-primary bg-orange-500 hover:bg-blue-600 text-white text-lg px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Ver vacantes disponibles
                  <svg
                    className="w-5 h-5 ml-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>

                <a
                  href="https://packsys.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline border-2 border-gray-300 text-gray-700 hover:bg-gray-800 hover:text-white hover:border-gray-800 text-lg px-8 py-3 rounded-xl hover:scale-105 transform transition-all duration-300"
                >
                  Visitar tienda
                </a>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Versión desktop: imagen de fondo con contenido superpuesto
        <>
          <div className="absolute inset-0 z-0 overflow-hidden">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {/* Overlay gradient */}
          <div className="absolute inset-0 z-10 bg-gradient-to-r from-blue-900/30 to-blue-800/20"></div>

          {/* Content desktop */}
          <div className="container relative z-20 mx-auto px-4">
            <div className="max-w-2xl animate-fade-in-up">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-8 leading-tight">
                Construye tu carrera laboral con Packsys
              </h1>

              <div className="flex flex-col sm:flex-row gap-4 pb-36">
                <Link
                  to="/vacantes"
                  className="btn-primary bg-orange-500 text-lg px-10 py-4 rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 hover:bg-blue-600 transition-all duration-300"
                >
                  Ver vacantes disponibles
                  <svg
                    className="w-5 h-5 ml-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>

                <a
                  href="https://packsys.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-10 py-4 rounded-xl hover:scale-105 transform transition-all duration-300"
                >
                  Visitar tienda
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
